import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiResponse } from '@shared/models/api-response.model';
import {
  AppSession,
  ModuleAccess,
  SESSION_FAILURE,
  SESSION_STATUS,
  SessionFailure,
  SessionLoadResult,
  SessionRole,
  SessionStatus,
  SessionWarehouse,
  isAppModule,
  isModuleAction,
} from '../models/session.model';

interface SessionRoleApiItem {
  id?: string | null;
  name?: string | null;
  description?: string | null;
}

interface ModuleAccessApiItem {
  code?: string | null;
  actions?: string[] | null;
}

interface SessionWarehouseApiItem {
  clave?: string | null;
  nombre?: string | null;
}

interface SessionApiItem {
  subject?: string | null;
  username?: string | null;
  email?: string | null;
  display_name?: string | null;
  full_name?: string | null;
  status?: string | null;
  has_web_access?: boolean | null;
  role?: SessionRoleApiItem | null;
  permissions?: string[] | null;
  modules?: ModuleAccessApiItem[] | null;
  erp?: { profileskore?: string | null } | null;
  last_login?: string | null;
  almacen?: SessionWarehouseApiItem | null;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  //* Services
  private readonly _http = inject(HttpClient);

  //* Variables
  private readonly _baseUrl: string = `${environment.urlBase}/api/v1/me`;

  //* Public Methods
  public getSession(): Observable<SessionLoadResult> {
    return this._http.get<ApiResponse<SessionApiItem>>(this._baseUrl).pipe(
      map((res) => this._toResult(res?.data ?? null)),
      catchError((err: HttpErrorResponse) => of(this._toFailure(this._toFailureCode(err.status)))),
    );
  }

  //* Private Methods
  private _toResult(item: SessionApiItem | null): SessionLoadResult {
    if (!item) return this._toFailure(SESSION_FAILURE.UNAVAILABLE);

    return { session: this._toAppSession(item), failure: null };
  }

  private _toFailure(failure: SessionFailure): SessionLoadResult {
    return { session: null, failure };
  }

  private _toFailureCode(status: number): SessionFailure {
    switch (status) {
      case HttpStatusCode.Unauthorized:
        return SESSION_FAILURE.UNAUTHORIZED;
      case HttpStatusCode.Forbidden:
        return SESSION_FAILURE.BLOCKED;
      case HttpStatusCode.UnprocessableEntity:
        return SESSION_FAILURE.NOT_PROVISIONED;
      default:
        return SESSION_FAILURE.UNAVAILABLE;
    }
  }

  private _toAppSession(item: SessionApiItem): AppSession {
    const role = this._toSessionRole(item.role);

    return {
      subject: item.subject ?? '',
      username: item.username ?? '',
      email: item.email ?? '',
      displayName: item.display_name ?? item.full_name ?? '',
      fullName: item.full_name ?? '',
      status: this._toSessionStatus(item.status, role),
      hasWebAccess: item.has_web_access ?? true,
      role,
      permissions: (item.permissions ?? []).filter(
        (permission): permission is string => typeof permission === 'string',
      ),
      modules: this._toModules(item.modules),
      profileskore: item.erp?.profileskore ?? null,
      lastLoginAt: item.last_login ?? null,
      warehouse: this._toWarehouse(item.almacen),
    };
  }

  private _toSessionRole(item: SessionRoleApiItem | null | undefined): SessionRole | null {
    if (!item?.name) return null;

    return { id: item.id ?? null, name: item.name, description: item.description ?? '' };
  }

  private _toWarehouse(item: SessionWarehouseApiItem | null | undefined): SessionWarehouse | null {
    if (!item?.clave && !item?.nombre) return null;

    return { code: item?.clave ?? '', name: item?.nombre ?? '' };
  }

  private _toModules(items: ModuleAccessApiItem[] | null | undefined): ModuleAccess[] {
    if (!Array.isArray(items)) return [];

    return items
      .filter((item): item is ModuleAccessApiItem => isAppModule(item?.code))
      .map((item) => ({
        code: item.code as ModuleAccess['code'],
        actions: (item.actions ?? []).filter(isModuleAction),
      }));
  }

  private _toSessionStatus(
    status: string | null | undefined,
    role: SessionRole | null,
  ): SessionStatus {
    if (status === SESSION_STATUS.BLOCKED) return SESSION_STATUS.BLOCKED;
    if (status === SESSION_STATUS.NO_ROLE || !role) return SESSION_STATUS.NO_ROLE;

    return SESSION_STATUS.ACTIVE;
  }
}
